import { useState, useEffect, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import cn from "classnames";
import { tagular } from "@cohesion/tagular";

import { useBestInTravel } from "@context/bestInTravel/2025/bestInTravelContext";

import { BIT_PROP_TYPES } from "@constants/best-in-travel/2025/proptypes";

import { Arrow } from "@icons/elsewhere";
import { LogoGlobe } from "@icons/logos";
import { Ad } from "@components/ad";
import { SailyLogo } from "@pageComponents/best-in-travel-2025/saily/sailyLogo";

function VideoWrapper({ src, children }) {
  const { isClient } = useBestInTravel();
  return (
    <div className="relative h-full">
      {isClient && src && (
        <video
          autoPlay
          loop
          poster={src?.poster}
          playsInline
          muted
          className="object-cover w-full h-full"
        >
          <source src={`${src.mp4}`} type="video/mp4" />
          <source src={`${src.webm}`} type="video/webm" />
        </video>
      )}
      <div className="absolute top-0 left-0 z-10 flex items-center justify-center w-full h-full">
        {children}
      </div>
    </div>
  );
}

VideoWrapper.propTypes = {
  src: PropTypes.shape({
    poster: PropTypes.string,
    mp4: PropTypes.string,
    webm: PropTypes.string,
  }),
  children: PropTypes.node.isRequired,
};

export function Hero({ category = null, campaign }) {
  const { handleScrollToMap, screen } = useBestInTravel();
  const destinationCategory = category?.name || null;
  const description = campaign?.description || null;
  const isMainExperience = !category;
  const [preventScroll, setPreventScroll] = useState(true);
  const [firstScroll, setFirstScroll] = useState(false);
  const [initialTransform, setInitialTransform] = useState(true);
  const [topOfPageFirst, setTopOfPageFirst] = useState(false);
  const [videoSrc, setVideoSrc] = useState(null);

  const heroSectionRef = useRef(null);
  const timeoutRef = useRef(null);
  let lastY = useRef(0);

  const handleScrollAndAnimation = () => {
    setInitialTransform(false);
    if (!firstScroll) {
      setPreventScroll(false);
      setFirstScroll(true);
      setTimeout(() => {
        handleScrollToMap();
      }, 1000);
    } else {
      handleScrollToMap();
    }
  };

  const handleClick = () => {
    handleScrollAndAnimation();
    tagular("click", {
      actionOutcome: "INTERNALLINK",
      outboundUrl: "#map",
      webElement: {
        location: "BIT_Hero",
        elementType: "LINK",
        text: "Arrow",
        position: "0",
      },
    });
  };

  function schedulePreventScrollReset(bool) {
    if (!timeoutRef.current) {
      timeoutRef.current = setTimeout(() => {
        setPreventScroll(bool);
        timeoutRef.current = null;
      }, 1000);
    }
  }

  const handleScroll = useCallback(
    event => {
      const container = document.getElementById("wrapperContainer");
      const topOfPage = container.scrollTop < 5;
      const isScrollingUp =
        event.deltaY !== undefined
          ? event.deltaY < 0
          : event.touches?.[0].clientY > lastY;

      if (event.touches) {
        lastY = event.touches[0].clientY;
      }

      const preventDefaultAndPropagate = () => {
        event.preventDefault();
        event.stopPropagation();
      };

      const handlePreventScroll = () => {
        preventDefaultAndPropagate();
        setInitialTransform(false);
        schedulePreventScrollReset(false);
        setTopOfPageFirst(false);
        return true;
      };

      const handleTopOfPageFirst = () => {
        preventDefaultAndPropagate();
        schedulePreventScrollReset(true);
        setInitialTransform(true);
        setTopOfPageFirst(false);
        return true;
      };

      const handleScrollingUpAtTop = () => {
        if (!timeoutRef.current) {
          timeoutRef.current = setTimeout(() => {
            setTopOfPageFirst(true);
            timeoutRef.current = null;
          }, 200);
        }
        return true;
      };

      return (
        (preventScroll && !isScrollingUp && handlePreventScroll()) ||
        (topOfPageFirst && handleTopOfPageFirst()) ||
        (isScrollingUp && topOfPage && handleScrollingUpAtTop()) ||
        true // Default case: normal scrolling behavior
      );
    },
    [preventScroll, schedulePreventScrollReset]
  );

  useEffect(() => {
    window.addEventListener("scroll", handleScroll, { passive: false });
    window.addEventListener("wheel", handleScroll, { passive: false });
    window.addEventListener("touchmove", handleScroll, { passive: false });
    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("wheel", handleScroll);
      window.removeEventListener("touchmove", handleScroll);
    };
  }, [handleScroll]);

  const animation = {
    transparentCircle: initialTransform
      ? "opacity-100 delay-200"
      : "opacity-10 delay-50",

    blueCircle: initialTransform ? "translate-y-[100%]" : "translate-y-[-5%]",

    globe: initialTransform
      ? "translate-y-[0%] opacity-100 max-h-auto"
      : "translate-y-[100px] opacity-0 max-h-0",

    arrow: initialTransform
      ? "sm:pointer-events-none opacity-0 md:opacity-100 lg:opacity-0 lg:hidden"
      : "opacity-100 lg:block",

    description: initialTransform ? "opacity-0" : "opacity-100 max-h-full",

    mainExperience: {
      content: initialTransform
        ? "bottom-0 translate-y-0"
        : "bottom-0 -translate-y-12 lg:translate-y-0",
    },

    categoryExperience: {
      content: initialTransform
        ? "bottom-0 translate-y-0"
        : "bottom-0 -translate-y-12 lg:translate-y-0",
    },
  };

  useEffect(() => {
    // Determine the orientation based on the screen size.
    // If the screen size is small ("sm"), use "portrait" orientation; otherwise, use "landscape".
    const orientation = screen === "sm" ? "portrait" : "landscape";

    // Filter the campaign media to include only media with the determined orientation.
    const selectedMedia = campaign.media.filter(
      media => media.orientation === orientation
    );

    // Set the video source to a randomly selected media item from selectedMedia.
    // If selectedMedia is empty, fallback to the first media item in the campaign media.
    const randomIndex = Math.floor(Math.random() * selectedMedia.length);

    setVideoSrc(
      selectedMedia.length > 0
        ? selectedMedia[randomIndex].src
        : campaign.media[0].src
    );
  }, []);

  return (
    <div
      ref={heroSectionRef}
      className="relative w-full overflow-hidden snap-start h-screen-dvh"
    >
      <VideoWrapper src={{ ...videoSrc }}>
        {/* TRANSPARENT CIRCLE */}
        <div
          className={cn(
            "w-full h-full rounded-t-pill absolute lg:w-[1400px] lg:aspect-square mx-auto lg:h-auto lg:rounded-full overflow-hidden left-1/2 transform -translate-x-1/2 top-36 lg:top-56 transition-opacity duration-1000 ease-in-out border border-white",
            animation?.transparentCircle
          )}
        />

        {/* BLUE CIRCLE */}
        <div
          className={cn(
            "bg-bit w-full h-full rounded-t-pill bg-opacity-85 bg-blue absolute lg:w-[1500px] lg:aspect-square lg:h-auto mx-auto lg:rounded-full overflow-hidden left-1/2 transform -translate-x-1/2 top-36 lg:top-56 transition-transform duration-1000 ease-in-out will-change-transform",
            animation?.blueCircle
          )}
        />

        {/* CONTENT CIRCLE */}
        <div className="relative z-10 flex flex-col items-center w-full h-screen-dvh">
          {/* TITLE, YEAR, CATEGORY OR DESCRIPTION WRAPPER  */}
          <div
            className={cn(
              "absolute transition-all duration-1000 ease-in-out flex flex-col items-center lg:justify-end h-[80dvh] lg:h-[75dvh]",
              isMainExperience
                ? animation?.mainExperience?.content
                : animation?.categoryExperience?.content
            )}
          >
            {/* GLOBE */}
            <div
              className={cn(
                "transition-all duration-1000 ease-in-out mt-8 mb-6 lg:mb-auto overflow-y-hidden",
                animation?.globe
              )}
            >
              <LogoGlobe className="w-12 h-12 text-white transition-all duration-1000 ease-in-out" />
            </div>
            {/* TITLE */}
            <h1 className="w-full text-[28px] lg:text-4xl font-semibold tracking-widest text-center text-white font-neue drop-shadow mt-auto">
              <div className="md:mb-6 lg:mb-10">2025</div>{" "}
              <div
                className={cn(
                  "font-instrument text-[110px] lg:text-[200px] overflow-ellipsis leading-[0.85] mb-4 text-white drop-shadow w-full text-center"
                )}
              >
                Best in Travel
              </div>
              {/* CATEGORY */}
              {isMainExperience && (
                <div className="flex items-center justify-center gap-3 pt-4 pb-4 mx-auto border-t border-white border-opacity-50 lg:pb-9 w-content lg:px-10">
                  <span className="text-xs font-light leading-none tracking-widest uppercase lg:text-sm font-neue">
                    Presented by{" "}
                  </span>
                  <span className="text-lg leading-none">
                    <Ad
                      placement="logo"
                      path="best-in-travel/logo"
                      className="w-[84px] h-9"
                      bgColor="bg-transparent"
                      native
                    >
                      {/* eslint-disable-next-line react/jsx-no-target-blank */}
                      <a
                        href="https://go.saily.site/aff_c?offer_id=101&aff_id=5957&aff_sub=bitlogo&url_id=1344"
                        aria-label="Saily Logo"
                        target="_blank"
                        rel="noopener nofollow"
                      >
                        <SailyLogo className="w-[84px] h-9" />
                      </a>
                    </Ad>
                  </span>
                </div>
              )}
              {destinationCategory && (
                <div
                  className={cn(
                    "uppercase font-bold font-rama text-[50px] lg:text-[72px] overflow-ellipsis leading-none text-white drop-shadow w-full text-center mb-3"
                  )}
                >
                  {" "}
                  {destinationCategory}
                </div>
              )}
            </h1>

            {/* DESCRIPTION */}
            {/* TODO -  Review - This description is too tight in the design to be responsive, it should be more spaced out. */}
            {description && (
              <div
                className={cn(
                  "font-neue font-normal container text-center mx-auto text-lg lg:text-xl text-white transition-all duration-1000 ease-in-out lg:w-2/3 max-h-0 mb-3",
                  animation?.description
                )}
              >
                {description}
              </div>
            )}

            <p
              className={cn(
                "w-1/2 text-center mx-auto mt-auto text-xl lg:text-3xl text-white transition-opacity duration-1000 ease-in-out",
                animation?.arrow
              )}
            >
              {/* ARROW */}
              <button type="button" onClick={() => handleClick()}>
                <Arrow className="block h-8 mx-auto rotate-90 lg:h-14" />
              </button>
            </p>
          </div>
        </div>
      </VideoWrapper>
      <style jsx>{`
        .bg-bit {
          background-repeat: no-repeat;
          background-size: contain;
          background-position: top;
          background-image: url(https://lonelyplanetstatic.imgix.net/marketing/best-in-travel/2025/circle-bg-texture.svg);
        }
      `}</style>
    </div>
  );
}

Hero.propTypes = {
  campaign: PropTypes.shape({
    title: PropTypes.string.isRequired,
    description: PropTypes.string,
    media: PropTypes.arrayOf(
      PropTypes.shape({
        type: PropTypes.oneOf(["video", "image"]).isRequired,
        orientation: PropTypes.oneOf(["portrait", "landscape"]).isRequired,
        src: PropTypes.shape({
          poster: PropTypes.string.isRequired,
          mp4: PropTypes.string.isRequired,
          webm: PropTypes.string.isRequired,
        }).isRequired,
      })
    ).isRequired,
  }).isRequired,
  category: BIT_PROP_TYPES.category,
};
